// src/views/SyncAudit/SyncRunDetail.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useParams, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import { Badge, Col, Container, Form, Row, Card, Button } from "react-bootstrap";
import DynamicBreadcrumb from "../header/breadcrumb";
import Loader from "../loader/loader";
import syncAuditApi from "../../services/syncAuditApi";

const fmt = (v) => (v ? new Date(v).toLocaleString() : "-");

function StatusBadge({ value }) {
  const v = value || "-";
  const bg =
    v === "SUCCESS"
      ? "success"
      : v === "RUNNING"
      ? "primary"
      : v === "NO_CHANGES"
      ? "info"
      : v === "FAILED"
      ? "danger"
      : v === "CANCELLED"
      ? "warning"
      : v === "SKIPPED"
      ? "secondary"
      : "secondary";
  return <Badge bg={bg}>{v}</Badge>;
}

// ✅ Normalize snake_case/camelCase
const normalizeBranchRow = (r) => {
  if (!r) return r;

  return {
    ...r,
    run_id: r.run_id ?? r.runId ?? r.runID,
    branch_id: r.branch_id ?? r.branchId ?? r.branchID ?? r.id,
    branch_name: r.branch_name ?? r.branchName ?? r.name,
    branch_code: r.branch_code ?? r.branchCode ?? r.code,
    status: r.status ?? r.syncStatus,
    started_at: r.started_at ?? r.startedAt,
    ended_at: r.ended_at ?? r.endedAt,
    duration_ms: r.duration_ms ?? r.durationMs,
    items_changed_count: r.items_changed_count ?? r.itemsChangedCount ?? r.itemsChanged ?? r.items,
    stock_changed_count: r.stock_changed_count ?? r.stockChangedCount ?? r.stockChanged ?? r.stock,
    outofstock_count: r.outofstock_count ?? r.outOfStockCount ?? r.oos,
    hq_lastdate_used: r.hq_lastdate_used ?? r.hqLastdateUsed ?? r.hqWindow ?? r.hqWindowUsed,
    error_message: r.error_message ?? r.errorMessage ?? r.error,

    // ✅ NEW COUNTS (from your new DB columns)
    api_total_received: r.api_total_received ?? r.apiTotalReceived ?? 0,
    api_unique_received: r.api_unique_received ?? r.apiUniqueReceived ?? 0,
    matched_count: r.matched_count ?? r.matchedCount ?? 0,
    unmatched_count: r.unmatched_count ?? r.unmatchedCount ?? 0,
    updated_count: r.updated_count ?? r.updatedCount ?? 0,
    price_updated_count: r.price_updated_count ?? r.priceUpdatedCount ?? 0,
    stock_updated_count: r.stock_updated_count ?? r.stockUpdatedCount ?? 0,
    skipped_count: r.skipped_count ?? r.skippedCount ?? 0,
    failed_count: r.failed_count ?? r.failedCount ?? 0,
  };
};

const normalizeChangeRow = (r) => {
  if (!r) return r;
  return {
    ...r,
    created_at: r.created_at ?? r.createdAt,
    change_type: r.change_type ?? r.changeType ?? r.type,
    item_code: r.item_code ?? r.itemCode ?? r.code,
    ref_code: r.ref_code ?? r.refCode,
    product_name: r.product_name ?? r.productName ?? r.name,
    unit_spec: r.unit_spec ?? r.unitSpec,
    unit_name: r.unit_name ?? r.unitName,
    old_value: r.old_value ?? r.oldValue,
    new_value: r.new_value ?? r.newValue,
    current_sell_price: r.current_sell_price ?? r.currentSellPrice,
    current_mrp: r.current_mrp ?? r.currentMrp,
  };
};

const extractPayload = (resp) => {
  if (!resp) return null;
  if (resp.rows && typeof resp.total !== "undefined") return resp;
  if (resp.data?.rows && typeof resp.data?.total !== "undefined") return resp.data;
  if (resp.data?.data?.rows && typeof resp.data?.data?.total !== "undefined") return resp.data.data;
  if (resp.data?.data && typeof resp.data?.data === "object") return resp.data.data;
  if (resp.data && typeof resp.data === "object") return resp.data;
  return resp;
};

const asNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function StatCard({ title, value, hint = null, variant = "light" }) {
  return (
    <Card className="h-100">
      <Card.Body className="py-2">
        <div className="text-muted" style={{ fontSize: 12 }}>
          {title}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          {value}
          {hint ? <span className="text-muted ms-2" style={{ fontSize: 12 }}>{hint}</span> : null}
        </div>
      </Card.Body>
    </Card>
  );
}

export default function SyncRunDetail() {
  const { runId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [run, setRun] = useState(null);
  const [branches, setBranches] = useState([]);

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [changesRows, setChangesRows] = useState([]);
  const [changesTotal, setChangesTotal] = useState(0);
  const [changesPagination, setChangesPagination] = useState({ page: 0, pageSize: 50 });
  const [changeType, setChangeType] = useState("");
  const [search, setSearch] = useState("");

  const pollRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const pathArray = [
    { name: "Reports", url: "" },
    { name: "GoFrugal Sync Audit", url: "/admin/gofrugal-sync" },
    { name: `Run #${runId}`, url: "" },
  ];

  const fmtHqWindow = (v) => {
    const s = String(v || "").trim();
    if (!s) return "-";
    const m = moment(s, "YYYYMMDDHHmmss", true);
    return m.isValid() ? m.format("DD/MM/YYYY hh:mm:ss A") : s;
  };

  const branchColumns = useMemo(
    () => [
      { field: "branch_id", headerName: "Branch ID", width: 95 },

      { field: "branch_name", headerName: "Branch", width: 200, renderCell: (p) => p?.row?.branch_name ?? "-" },

      { field: "branch_code", headerName: "Code", width: 85, renderCell: (p) => p?.row?.branch_code ?? "-" },

      { field: "status", headerName: "Status", width: 120, renderCell: (p) => <StatusBadge value={p?.row?.status} /> },

      {
        field: "started_at",
        headerName: "Start",
        width: 175,
        renderCell: (p) => (p?.row?.started_at ? fmt(p.row.started_at) : "-"),
      },
      {
        field: "ended_at",
        headerName: "End",
        width: 175,
        renderCell: (p) => (p?.row?.ended_at ? fmt(p.row.ended_at) : "-"),
      },
      {
        field: "duration_ms",
        headerName: "Duration (s)",
        width: 120,
        renderCell: (p) => {
          const ms = p?.row?.duration_ms;
          return ms ? Math.round(ms / 1000) : "-";
        },
      },

      // ✅ existing (kept)
      { field: "items_changed_count", headerName: "Items", width: 85, renderCell: (p) => p?.row?.items_changed_count ?? 0 },
      { field: "stock_changed_count", headerName: "Stock", width: 85, renderCell: (p) => p?.row?.stock_changed_count ?? 0 },

      // ✅ NEW CLARITY COUNTS
      { field: "api_total_received", headerName: "API", width: 80, renderCell: (p) => p?.row?.api_total_received ?? 0 },
      { field: "api_unique_received", headerName: "Unique", width: 85, renderCell: (p) => p?.row?.api_unique_received ?? 0 },
      { field: "matched_count", headerName: "Matched", width: 90, renderCell: (p) => p?.row?.matched_count ?? 0 },
      { field: "unmatched_count", headerName: "Unmatched", width: 100, renderCell: (p) => p?.row?.unmatched_count ?? 0 },
      { field: "updated_count", headerName: "Updated", width: 90, renderCell: (p) => p?.row?.updated_count ?? 0 },
      { field: "stock_updated_count", headerName: "StockUpd", width: 95, renderCell: (p) => p?.row?.stock_updated_count ?? 0 },
      { field: "price_updated_count", headerName: "PriceUpd", width: 95, renderCell: (p) => p?.row?.price_updated_count ?? 0 },
      { field: "skipped_count", headerName: "Skipped", width: 90, renderCell: (p) => p?.row?.skipped_count ?? 0 },
      { field: "failed_count", headerName: "Failed", width: 85, renderCell: (p) => p?.row?.failed_count ?? 0 },

      // ✅ real OOS
      { field: "outofstock_count", headerName: "OOS", width: 80, renderCell: (p) => p?.row?.outofstock_count ?? 0 },

      {
        field: "hq_lastdate_used",
        headerName: "HQ Window",
        width: 190,
        renderCell: (p) => {
          const v = p?.row?.hq_lastdate_used;
          return v ? fmtHqWindow(v) : "-";
        },
      },

      { field: "error_message", headerName: "Error", width: 240, renderCell: (p) => p?.row?.error_message ?? "-" },
    ],
    []
  );

  const changesColumns = useMemo(
    () => [
      {
        field: "created_at",
        headerName: "Time",
        width: 190,
        renderCell: (p) => (p?.row?.created_at ? fmt(p.row.created_at) : "-"),
      },
      { field: "change_type", headerName: "Type", width: 120, renderCell: (p) => p?.row?.change_type ?? "-" },
      { field: "item_code", headerName: "Item Code", width: 130, renderCell: (p) => p?.row?.item_code ?? "-" },
      { field: "ref_code", headerName: "Ref Code", width: 120, renderCell: (p) => p?.row?.ref_code ?? "-" },
      { field: "product_name", headerName: "Product", width: 260, renderCell: (p) => p?.row?.product_name ?? "-" },
      {
        field: "unit",
        headerName: "Unit",
        width: 130,
        renderCell: (p) => {
          const spec = p?.row?.unit_spec;
          const unit = p?.row?.unit_name;
          if (!spec && !unit) return "-";
          return `${spec || ""} ${unit || ""}`.trim();
        },
      },
      { field: "old_value", headerName: "Old", width: 160, renderCell: (p) => p?.row?.old_value ?? "-" },
      { field: "new_value", headerName: "New", width: 160, renderCell: (p) => p?.row?.new_value ?? "-" },
      { field: "current_sell_price", headerName: "Current Sell", width: 130, renderCell: (p) => p?.row?.current_sell_price ?? "-" },
      { field: "current_mrp", headerName: "Current MRP", width: 120, renderCell: (p) => p?.row?.current_mrp ?? "-" },
    ],
    []
  );

  const loadRun = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [runResp, branchesResp] = await Promise.all([
        syncAuditApi.getRun({ runId }),
        syncAuditApi.listRunBranches({ runId }),
      ]);

      const runPayload = extractPayload(runResp);
      const branchesPayload = extractPayload(branchesResp);

      const runRow = runPayload?.data ? runPayload.data : runPayload;
      const branchList = Array.isArray(branchesPayload) ? branchesPayload : branchesPayload?.data ?? branchesPayload;

      const normalizedBranches = (Array.isArray(branchList) ? branchList : []).map(normalizeBranchRow);

      setRun(runRow || null);
      setBranches(normalizedBranches);

      // keep selected branch if possible
      setSelectedBranch((prev) => {
        if (prev?.branch_id) {
          const found = normalizedBranches.find((x) => String(x.branch_id) === String(prev.branch_id));
          if (found) return found;
        }
        const pick =
          normalizedBranches.find(
            (r) =>
              asNum(r.updated_count) > 0 ||
              asNum(r.unmatched_count) > 0 ||
              asNum(r.failed_count) > 0 ||
              asNum(r.items_changed_count) > 0 ||
              asNum(r.stock_changed_count) > 0
          ) ||
          normalizedBranches[0] ||
          null;
        return pick;
      });
    } catch (err) {
      console.error(err);
      setRun(null);
      setBranches([]);
      setSelectedBranch(null);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadChanges = async ({ silent = false } = {}) => {
    if (!selectedBranch?.branch_id) return;

    if (!silent) setLoading(true);
    try {
      const resp = await syncAuditApi.listRunBranchChanges({
        runId,
        branchId: selectedBranch.branch_id,
        limit: changesPagination.pageSize,
        page: changesPagination.page,
        changeType: changeType || null,
        search: search || null,
      });

      const payload = extractPayload(resp);
      const rawRows = Array.isArray(payload?.rows) ? payload.rows : [];
      const normalized = rawRows.map(normalizeChangeRow);

      setChangesRows(normalized);
      setChangesTotal(Number(payload?.total || 0));
    } catch (err) {
      console.error(err);
      setChangesRows([]);
      setChangesTotal(0);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // initial load
  useEffect(() => {
    loadRun();
    // eslint-disable-next-line
  }, [runId]);

  // changes load (server pagination)
  useEffect(() => {
    loadChanges();
    // eslint-disable-next-line
  }, [selectedBranch?.branch_id, changesPagination.page, changesPagination.pageSize, changeType]);

  // ✅ debounce search to avoid too many requests while typing
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setChangesPagination((p) => ({ ...p, page: 0 }));
      loadChanges();
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line
  }, [search]);

  // ✅ LIVE polling (every 3 seconds while RUNNING)
  useEffect(() => {
    const isRunning =
      (run?.status && String(run.status).toUpperCase() === "RUNNING") ||
      (Array.isArray(branches) && branches.some((b) => String(b.status).toUpperCase() === "RUNNING"));

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    if (isRunning) {
      pollRef.current = setInterval(async () => {
        await loadRun({ silent: true });
        // if selected branch is running, refresh changes also
        const sb = selectedBranch;
        if (sb?.status && String(sb.status).toUpperCase() === "RUNNING") {
          await loadChanges({ silent: true });
        }
      }, 3000);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [run?.status, branches, selectedBranch?.branch_id]);

  const selected = selectedBranch || {};

  return (
    <Container className="mt-4 mb-4">
      <DynamicBreadcrumb pathArray={pathArray} />

      <Box className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="col-6">Run Details #{runId}</h5>

        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => loadRun()}>
            Refresh
          </Button>
          <Button variant="secondary" onClick={() => navigate("/admin/gofrugal-sync")}>
            Back
          </Button>
        </div>
      </Box>

      {loading && <Loader />}

      {run && (
        <Row className="mb-3">
          <Col md={12}>
            <Card>
              <Card.Body>
                <Row>
                  <Col md={2}>
                    <div className="text-muted">Status</div>
                    <div>
                      <StatusBadge value={run.status ?? run.status} />
                    </div>
                  </Col>

                  <Col md={3}>
                    <div className="text-muted">Start</div>
                    <div>{fmt(run.started_at ?? run.startedAt)}</div>
                  </Col>

                  <Col md={3}>
                    <div className="text-muted">End</div>
                    <div>{fmt(run.ended_at ?? run.endedAt)}</div>
                  </Col>

                  <Col md={2}>
                    <div className="text-muted">Branches</div>
                    <div>{(run.total_branches ?? run.totalBranches) ?? 0}</div>
                  </Col>

                  <Col md={2}>
                    <div className="text-muted">Items Changed</div>
                    <div>{(run.total_items_changed ?? run.totalItemsChanged) ?? 0}</div>
                  </Col>
                </Row>

                {(run.error_message ?? run.errorMessage) && (
                  <div className="mt-2 text-danger" style={{ whiteSpace: "pre-wrap" }}>
                    {run.error_message ?? run.errorMessage}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Row>
        <Col md={12}>
          <h6 className="mb-2">Branch-wise Tracking</h6>
          <Box>
            <DataGrid
              rows={Array.isArray(branches) ? branches : []}
              columns={branchColumns}
              getRowId={(r) => `${r.run_id}-${r.branch_id}`}
              getRowHeight={() => "auto"}
              disableRowSelectionOnClick
              onRowClick={(params) => {
                setSelectedBranch(params.row);
                setChangesPagination((p) => ({ ...p, page: 0 }));
              }}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  printOptions: { disableToolbarButton: true },
                  csvOptions: { disableToolbarButton: false },
                },
              }}
              sx={{ [`& .${gridClasses.cell}`]: { py: 0.5 } }}
            />
          </Box>
        </Col>
      </Row>

      {/* ✅ Selected branch summary cards for non-tech clarity */}
      {selectedBranch && (
        <Row className="mt-3 g-2">
          <Col md={12}>
            <Card>
              <Card.Body className="py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Selected Branch
                    </div>
                    <div style={{ fontWeight: 700 }}>
                      {selected.branch_name || "-"}{" "}
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        (ID: {selected.branch_id})
                      </span>
                    </div>
                  </div>
                  <div>
                    <StatusBadge value={selected.status} />
                  </div>
                </div>

                <Row className="mt-2 g-2">
                  <Col md={2}><StatCard title="API Received" value={asNum(selected.api_total_received)} /></Col>
                  <Col md={2}><StatCard title="Unique" value={asNum(selected.api_unique_received)} /></Col>
                  <Col md={2}><StatCard title="Matched" value={asNum(selected.matched_count)} /></Col>
                  <Col md={2}><StatCard title="Unmatched" value={asNum(selected.unmatched_count)} /></Col>
                  <Col md={2}><StatCard title="Updated" value={asNum(selected.updated_count)} /></Col>
                  <Col md={2}><StatCard title="Skipped" value={asNum(selected.skipped_count)} /></Col>
                  <Col md={2}><StatCard title="Stock Updated" value={asNum(selected.stock_updated_count)} /></Col>
                  <Col md={2}><StatCard title="Price Updated" value={asNum(selected.price_updated_count)} /></Col>
                  <Col md={2}><StatCard title="Out of Stock" value={asNum(selected.outofstock_count)} /></Col>
                  <Col md={2}><StatCard title="Failed" value={asNum(selected.failed_count)} /></Col>
                  <Col md={4}>
                    <Card className="h-100">
                      <Card.Body className="py-2">
                        <div className="text-muted" style={{ fontSize: 12 }}>HQ Window Used</div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>
                          {selected.hq_lastdate_used ? fmtHqWindow(selected.hq_lastdate_used) : "-"}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {selected.error_message ? (
                  <div className="mt-2 text-danger" style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                    {selected.error_message}
                  </div>
                ) : null}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Row className="mt-4">
        <Col md={12}>
          <h6 className="mb-2">
            Change Details {selectedBranch?.branch_name ? `— ${selectedBranch.branch_name}` : ""}
          </h6>

          <Row className="mb-2">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Change Type</Form.Label>
                <Form.Select value={changeType} onChange={(e) => { setChangeType(e.target.value); setChangesPagination(p => ({...p, page: 0})); }}>
                  <option value="">All</option>
                  <option value="PRICE">PRICE</option>
                  <option value="STOCK">STOCK</option>
                  <option value="NEW_ITEM">NEW_ITEM</option>
                  <option value="SKIPPED">SKIPPED</option>
                  <option value="FAILED">FAILED</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Search (item_code / ref_code / product)</Form.Label>
                <Form.Control
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="eg: 10123 or Rice"
                />
              </Form.Group>
            </Col>

            <Col md={5} className="d-flex gap-2 align-items-end justify-content-end">
              <Button
                variant="outline-primary"
                onClick={() => {
                  setChangesPagination((p) => ({ ...p, page: 0 }));
                  loadChanges();
                }}
              >
                Refresh Changes
              </Button>
            </Col>
          </Row>

          <Box>
            <DataGrid
              rows={Array.isArray(changesRows) ? changesRows : []}
              columns={changesColumns}
              getRowId={(r) => r.id}
              rowsPerPageOptions={[25, 50, 100, 200]}
              paginationMode="server"
              rowCount={changesTotal}
              paginationModel={changesPagination}
              onPaginationModelChange={setChangesPagination}
              loading={loading}
              disableRowSelectionOnClick
              sx={{ [`& .${gridClasses.cell}`]: { py: 0.5 } }}
            />
          </Box>
        </Col>
      </Row>
    </Container>
  );
}
